import { useState, useEffect } from "react";
import { db } from "@/lib/firebase";
import {
  collection,
  query,
  where,
  addDoc,
  serverTimestamp,
  orderBy,
  onSnapshot,
  updateDoc,
  doc
} from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export interface BrokerMessage {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  content: string;
  sentAt: any;
  readAt?: any;
}

export interface BrokerConversation {
  id: string;
  participantId: string;
  participantName: string;
  participantType: 'seller' | 'buyer' | 'partner';
  lastMessage?: string;
  lastMessageAt?: any;
  unreadCount: number;
}

export function useBrokerMessages() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [conversations, setConversations] = useState<BrokerConversation[]>([]);
  const [messages, setMessages] = useState<BrokerMessage[]>([]);
  const [activeConversation, setActiveConversation] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Fetch conversations
  useEffect(() => {
    if (!user) return;

    const convsRef = collection(db, "brokerConversations");
    const convsQuery = query(
      convsRef,
      where("brokerId", "==", user.id),
      orderBy("lastMessageAt", "desc")
    );

    const unsubscribe = onSnapshot(convsQuery, (snapshot) => {
      const convsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BrokerConversation[];
      setConversations(convsData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching conversations:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  // Fetch messages for active conversation
  useEffect(() => {
    if (!activeConversation) {
      setMessages([]);
      return;
    }

    const msgsRef = collection(db, "brokerMessages");
    const msgsQuery = query(
      msgsRef,
      where("conversationId", "==", activeConversation),
      orderBy("sentAt", "asc")
    );

    const unsubscribe = onSnapshot(msgsQuery, (snapshot) => {
      const msgsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BrokerMessage[];
      setMessages(msgsData);

      // Mark messages as read
      msgsData.forEach(async (msg) => {
        if (msg.senderId !== user?.id && !msg.readAt) {
          await updateDoc(doc(db, "brokerMessages", msg.id), {
            readAt: serverTimestamp()
          });
        }
      });
    }, (error) => {
      console.error("Error fetching messages:", error);
    });

    return () => unsubscribe();
  }, [activeConversation, user]);

  const sendMessage = async (content: string) => {
    if (!user || !activeConversation || !content.trim()) return false;

    setSending(true);
    try {
      // Add message
      await addDoc(collection(db, "brokerMessages"), {
        conversationId: activeConversation,
        senderId: user.id,
        senderName: user.displayName || user.email || "Broker",
        content: content.trim(),
        sentAt: serverTimestamp()
      });

      // Update conversation with last message
      await updateDoc(doc(db, "brokerConversations", activeConversation), {
        lastMessage: content.trim().substring(0, 100),
        lastMessageAt: serverTimestamp()
      });

      return true;
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Send failed",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
      return false;
    } finally {
      setSending(false);
    }
  };

  const createConversation = async (
    participantId: string,
    participantName: string,
    participantType: 'seller' | 'buyer' | 'partner'
  ) => {
    if (!user) return null;

    try {
      const convRef = await addDoc(collection(db, "brokerConversations"), {
        brokerId: user.id,
        brokerName: user.displayName || user.email,
        participantId,
        participantName,
        participantType,
        unreadCount: 0,
        createdAt: serverTimestamp(),
        lastMessageAt: serverTimestamp()
      });

      setActiveConversation(convRef.id);
      return convRef.id;
    } catch (error) {
      console.error("Error creating conversation:", error);
      toast({
        title: "Error",
        description: "Failed to create conversation. Please try again.",
        variant: "destructive"
      });
      return null;
    }
  };

  return {
    conversations,
    messages,
    activeConversation,
    setActiveConversation,
    loading,
    sending,
    sendMessage,
    createConversation
  };
}
